"use client";

import { useMemo, useState } from "react";
import { StaticImageData } from "next/image";
import { ArrowDown } from "lucide-react";
import OverlayCard from "../shared/card/overlay-card";
import sticker9 from "../../../public/sticker9.png";
import sticker10 from "../../../public/sticker10.png";
import SectionTitle from "../shared/SectionTitle";

type Img = {
  id: number;
  imageUrl: string | StaticImageData;
};

type MenuSectionProps = {
  title1: string;
  title2: string;
  description: string;
  imageList: Img[];
  pdfUrl: string;
};

export default function IlNostroMenu({
  title1,
  title2,
  description,
  imageList,
  pdfUrl,
}: MenuSectionProps) {
  const images = useMemo(
    () =>
      (imageList || []).map((i) =>
        typeof i.imageUrl === "string" ? i.imageUrl : i.imageUrl.src
      ),
    [imageList]
  );

  const [overlayItem, setOverlayItem] = useState<{
    title?: string;
    fullDescription?: string;
    carouselImages?: string[];
  } | null>(null);

  return (
    <section className="py-24 pt-0 px-8 md:px-12">
      <div className="text-center max-w-3xl mx-auto mb-12">
        <SectionTitle
          title1={title1}
          title2={title2}
          leftIcon={sticker9}
          rightIcon={sticker10}
          iconSizeLeft={150}
          iconSizeRight={150}
          hideIconOnMobile="right"
        />
        <p className="text-gray-600 px-4">{description}</p>
      </div>

      <div className="flex flex-col md:flex-row gap-10 max-w-[1150px] mx-auto items-center md:items-stretch justify-center">
        {/* Card: Download menu */}
        <div className="flex flex-col text-(--foreground) border-x border-(--card-foreground) p-6 w-full max-w-[320px] md:flex-1">
          <h3 className="text-xl font-bold text-(--card-foreground) mb-4">
            Scopri il nostro menù
          </h3>
          <p className="text-gray-700 text-md leading-relaxed mb-8 flex-1">
            Scarica il PDF completo con tutti i pasti e le pietanze previsti
            durante l'anno scolastico. Troverai la suddivisione settimanale,
            indicazioni su eventuali alternative per diete speciali e tutte le
            informazioni utili per conoscere ciò che i bambini consumano ogni
            giorno a scuola.
          </p>
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 text-md font-semibold bg-(--card-foreground) text-(--background) rounded-full hover:bg-[#0a9cd8] transition mt-auto"
          >
            <ArrowDown className="w-4 h-4" />
            Scarica il menù
          </a>
        </div>

        {/* Card: Cucina interna */}
        <div className="flex flex-col text-(--foreground) border-x border-(--card-foreground) p-6 w-full max-w-[320px] md:flex-1">
          <h3 className="text-xl font-bold text-(--card-foreground) mb-4">
            La cucina interna!
          </h3>
          <p className="text-gray-700 text-md leading-relaxed flex-1">
            È presente una cucina interna, moderna e attrezzata, che consente la preparazione quotidiana dei pasti in un ambiente controllato e conforme alle normative vigenti, nel rispetto del sistema di autocontrollo HACCP per garantire la sicurezza alimentare.
            <br /><br />
            Sono inoltre previste variazioni personalizzate del menù in caso di allergie, intolleranze o specifiche necessità certificate, a tutela della salute e del benessere di ogni bambino.
          </p>
        </div>
      </div>

      <OverlayCard
        selectedItem={overlayItem}
        onClose={() => setOverlayItem(null)}
        maxWidthClass="max-w-[1000px]"
        maxHeightClass="max-h-[90vh]"
      />
    </section>
  );
}